import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {Grid, InputAdornment, MenuItem, Paper, TextField} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import debounce from 'lodash/debounce';
import type * as React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import EmployeeCapacityMonthSelection from '@/components/Capacity/EmployeeCapacityMonthSelection';
import type {CapacityConfig} from '@/types/CapacityConfig';

export type FilterSearchType = 'people' | 'projects';

export type FilterSearch = {
    query : string;
    type : FilterSearchType;
};

type Props = {
    fiscalMonth : string;
    setFiscalMonth : (fiscalMonth : string) => void;
    filterSearch : FilterSearch;
    onFilterSearchChange : (filterSearch : FilterSearch) => void;
    capacityConfig : CapacityConfig;
    defaultCapacityYearMonth : string;
};

const CapacityAppBar : React.FC<Props> = (
    {
        fiscalMonth,
        setFiscalMonth,
        filterSearch,
        onFilterSearchChange,
        capacityConfig,
        defaultCapacityYearMonth,
    } : Props
) => {
    const [query, setQuery] = useState(filterSearch.query);
    const queryField = useRef<HTMLInputElement>();

    useEffect(() => {
        setQuery(filterSearch.query);
    }, [filterSearch.query]);

    const debouncedSearch = useCallback(debounce(
        (query : string, type : FilterSearchType) => {
            onFilterSearchChange({type, query});
        },
        400
    ), [onFilterSearchChange]);

    const handleQueryChange = (query : string) => {
        setQuery(query);
        debouncedSearch(query, filterSearch.type);
    };

    const handleQueryKeyDown = (event : React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            handleQueryChange('');
        }
    };

    useEffect(() => {
        const handleSearchHotkey = (event : KeyboardEvent) => {
            if ((!event.ctrlKey && !event.metaKey) || event.code !== 'KeyF' || !queryField.current) {
                return;
            }

            event.preventDefault();
            queryField.current.focus();
            window.requestAnimationFrame(() => {
                window.scrollTo(0, 0);
            });
        };

        window.addEventListener('keydown', handleSearchHotkey);

        return () => {
            window.removeEventListener('keydown', handleSearchHotkey);
        };
    }, []);

    return (
        //className={classNames(classes.root, className)}
        <Paper sx={{border: 'none'}} elevation={0}>
            <Grid container spacing={2} alignItems="center">
                <Grid item>
                    {fiscalMonth && defaultCapacityYearMonth && <EmployeeCapacityMonthSelection
                        fiscalMonth={fiscalMonth}
                        setFiscalMonth={setFiscalMonth}
                        capacityConfig={capacityConfig}
                        defaultCapacityYearMonth={defaultCapacityYearMonth}
                    />}
                </Grid>
                <Grid item xs={12} md/>
                <Grid item>
                    <TextField
                        margin="dense"
                        variant="outlined"
                        value={query}
                        onChange={event => {
                            handleQueryChange(event.target.value);
                        }}
                        onKeyDown={handleQueryKeyDown}
                        inputRef={queryField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            handleQueryChange('');
                                        }}
                                        disabled={query === ''}
                                        edge="end"
                                    >
                                        <ClearIcon/>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item>
                    <TextField
                        margin="dense"
                        label="In"
                        value={filterSearch.type}
                        variant="outlined"
                        onChange={event => {
                            onFilterSearchChange({
                                type: event.target.value as FilterSearchType,
                                query: filterSearch.query,
                            });
                        }}
                        select
                    >
                        <MenuItem value="people">People</MenuItem>
                    </TextField>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default CapacityAppBar;
